import moment from 'moment';

import {financialYear} from '../../../interface/Units';
import useCompanyStore from '../../../store/companyStore/companyDetailsStore';

/**
 * Get financial year start and end dates based on fiscal year preference
 * @param fiscalYearPreference - The fiscal year preference (e.g., 'AprMar', 'JanDec', 'JulJun', 'OctSep')
 * @param selectedYear - Optional selected year (e.g., '2025' or '2025-2026')
 * @returns Object with startDate and endDate as moment objects
 */
export const getFinancialYearDates = (
  fiscalYearPreference?: string,
  selectedYear?: string
): {startDate: moment.Moment; endDate: moment.Moment} => {
  // Get fiscal year preference from store if not provided
  const {preUnits} = useCompanyStore.getState();
  const fiscalYear = fiscalYearPreference ?? preUnits?.financialYear;

  const splitMonth = (str?: string) => {
    const [startMonth, endMonth] = (str ?? financialYear.AprMar).split(
      /(?=[A-Z][a-z])/
    );

    return {startMonth, endMonth};
  };

  const {startMonth, endMonth} = splitMonth(fiscalYear);

  // Parse selected year or use current year
  let yearStart: number;
  let yearEnd: number;

  if (selectedYear) {
    const splitYear = selectedYear.split('-');
    const [startYear, endYear] = splitYear?.[1]
      ? [splitYear[0], splitYear[1]]
      : [splitYear?.[0], (Number(splitYear?.[0]) + 1).toString()];

    yearStart = parseInt(startYear);
    yearEnd = parseInt(endYear);
  } else {
    // Use current date to determine financial year
    const currentDate = moment();
    const currentYear = currentDate.year();
    const currentMonth = currentDate.month() + 1; // moment months are 0-indexed

    const startMonthNum = moment(startMonth, 'MMM').month() + 1;
    const endMonthNum = moment(endMonth, 'MMM').month() + 1;

    const isSpanningFY = startMonthNum > endMonthNum;
    yearStart = currentMonth >= startMonthNum ? currentYear : currentYear - 1;
    yearEnd = isSpanningFY ? yearStart + 1 : yearStart;
  }

  // Create start and end dates
  const startDate = moment(
    `${yearStart}-${startMonth}-01`,
    'YYYY-MMM-DD'
  ).startOf('month');
  const endDate = moment(`${yearEnd}-${endMonth}-01`, 'YYYY-MMM-DD').endOf(
    'month'
  );

  return {startDate, endDate};
};

/**
 * Validate if a date is within the financial year range
 * @param date - The date to validate (moment object)
 * @param fiscalYearPreference - The fiscal year preference
 * @param selectedYear - Optional selected year
 * @returns boolean indicating if date is valid
 */
export const isDateWithinFinancialYear = (
  date: moment.Moment,
  fiscalYearPreference?: string,
  selectedYear?: string
): boolean => {
  const {startDate, endDate} = getFinancialYearDates(
    fiscalYearPreference,
    selectedYear
  );

  return (
    date.isSameOrAfter(startDate, 'day') && date.isSameOrBefore(endDate, 'day')
  );
};

/**
 * Get disabled date function for date picker
 * @param fiscalYearPreference - The fiscal year preference
 * @param selectedYear - Optional selected year
 * @param relatedFieldName - Optional field name for related date (for end date validation)
 * @param isStartDate - Whether this is the start date field
 * @returns Function to check if a date should be disabled
 */
export const getFinancialYearDisabledDate = (
  fiscalYearPreference?: string,
  selectedYear?: string,
  relatedFieldName?: string,
  isStartDate: boolean = true,
  getFieldValue?: (name: string) => moment.Moment | string | undefined
): ((current: moment.Moment) => boolean) => {
  return (current: moment.Moment) => {
    const {startDate, endDate} = getFinancialYearDates(
      fiscalYearPreference,
      selectedYear
    );

    // Disable dates outside financial year
    if (current.isBefore(startDate, 'day') || current.isAfter(endDate, 'day')) {
      return true;
    }

    // For end date, ensure it's after start date
    if (!isStartDate && relatedFieldName && getFieldValue) {
      const startDateValue = getFieldValue(relatedFieldName);
      if (startDateValue && moment.isMoment(startDateValue)) {
        if (current.isSameOrBefore(startDateValue, 'day')) {
          return true;
        }
      }
    }

    return false;
  };
};
